/* External */
import { createBrowserHistory, createMemoryHistory } from 'history';

/* Ott-common */
import { ContentType } from '@dtvgo/backend-service';
import { EntityName } from '@dtvgo/backend-service/normalize';

/* Utils */
import { getLink, Routes } from './router';
import { shouldUseLegoPlayer } from './legoPlayerGate';
import { getWebAction, isLanderCollection } from './metaDataUtils';
import { GTM_EVENT_NAMES } from './googleTags/constants';
import { formatPlaySource, getParentLander, transmissionContentType } from './googleTags/utils';
import { getPlaySourcePage } from './trackSourceConviva';

/* Constants */
export const history = import.meta.env.BASE_URL.startsWith('.')
  ? createMemoryHistory()
  : createBrowserHistory();

/**
 * @method redirectTo
 * @description Method that redirect to App route.
 * @param {string} url
 */
export function redirectTo(url, state, replace) {
  if (url) {
    if (replace) {
      history.replace(url, state);
    } else {
      history.push(url, state);
    }
  }
}

function getPlayerRouteParams(entityInstance) {
  if (entityInstance?.schema) {
    switch (entityInstance?.schema) {
      case EntityName.CONTENT: {
        return {
          routeToRedirect: Routes.Private.Player.Vod,
          params: { contentId: entityInstance?.vrioAssetId },
        };
      }
      case EntityName.SCHEDULE:
      case EntityName.CHANNEL: {
        return {
          routeToRedirect: Routes.Private.Player.Live,
          params: { channelId: entityInstance?.channelId },
        };
      }
      default: {
        return {};
      }
    }
  }
  if (entityInstance.type === ContentType.CHANNEL) {
    return {
      routeToRedirect: Routes.Private.Player.Live,
      params: { channelId: entityInstance?.channelId },
    };
  }
  if (entityInstance.type === ContentType.EPISODE) {
    return {
      routeToRedirect: Routes.Private.Player.Vod,
      params: { contentId: entityInstance?.vrioAssetId },
    };
  }
  return {};
}

/* lego player: every playback click lands in the Solid player instead of /private/player. */
function redirectToLegoPlayer({
  entityInstance,
  query,
  origin,
  finalState,
  replace,
  isLive,
}) {
  redirectTo(
    getLink(Routes.Private.LegoPlayer, {
      query: {
        ...query,
        contentId: isLive ? entityInstance?.channelId : entityInstance?.vrioAssetId,
        isLive: isLive ? 'true' : 'false',
        origin,
      },
    }),
    finalState,
    replace,
  );
}

export function redirectToPlayer(entityInstance, {
  page = '',
  section = '',
  replace = false,
  query = {},
  state = {},
}) {
  const origin = [page, section].filter(Boolean).join('-') || undefined;

  const { routeToRedirect, params } = getPlayerRouteParams(entityInstance);

  const finalState = {
    ...state,
    ...(state?.actionOrigin ? {} : { actionOrigin: entityInstance?.actionOrigin || '' }),
  };

  if (!routeToRedirect) return;

  const isLive = routeToRedirect === Routes.Private.Player.Live;

  if (shouldUseLegoPlayer({ isLive, channelId: params.channelId })) {
    redirectToLegoPlayer({
      entityInstance,
      query,
      origin,
      finalState,
      replace,
      isLive,
    });
    return;
  }

  redirectTo(
    getLink(routeToRedirect, {
      ...params,
      query: {
        ...params.query,
        ...query,
        origin,
      },
    }),
    finalState,
    replace,
  );
}

/**
 * @method getDetailRouteFromSchema
 * @description Helper function to get route details based on entity schema
 * @param {Object} entityInstance
 * @param {Object} parentRoute
 * @returns {Object|null} Route details or undefined
 */
function getDetailRouteFromSchema(entityInstance, parentRoute) {
  switch (entityInstance?.schema) {
    case EntityName.CONTENT: {
      return {
        detailPageUrl: parentRoute.Vod,
        params: { contentId: entityInstance?.vrioAssetId },
      };
    }
    case EntityName.SHOW: {
      return {
        detailPageUrl: parentRoute.Show,
        params: { showId: entityInstance?.vrioAssetId },
      };
    }
    case EntityName.CHANNEL:
    case EntityName.SCHEDULE: {
      return {
        detailPageUrl: parentRoute.Live,
        params: {
          channelId: entityInstance?.channelId,
          query: { startTime: entityInstance?.startTime?.toISOString() },
        },
      };
    }
    default: {
      return undefined;
    }
  }
}

/**
 * @method getDetailRouteFromType
 * @description Helper function to get route details based on entity type
 * @param {Object} entityInstance
 * @param {Object} parentRoute
 * @returns {Object|null} Route details or undefined
 */
function getDetailRouteFromType(entityInstance, parentRoute) {
  const isVodContent = entityInstance?.type === ContentType.MOVIE
    || entityInstance?.type === ContentType.EVENT
    || entityInstance?.type === ContentType.EPISODE;

  if (isVodContent) {
    return {
      detailPageUrl: parentRoute.Vod,
      params: { contentId: entityInstance?.vrioAssetId },
    };
  }

  if (entityInstance?.type === ContentType.SERIE) {
    return {
      detailPageUrl: parentRoute.Show,
      params: { showId: entityInstance?.vrioAssetId },
    };
  }

  const isLiveContent = (entityInstance?.startTime && entityInstance?.endTime)
    || entityInstance?.type === ContentType.CHANNEL;

  if (isLiveContent) {
    return {
      detailPageUrl: parentRoute.Live,
      params: {
        channelId: entityInstance?.channelId,
        query: { startTime: entityInstance?.startTime },
      },
    };
  }

  return undefined;
}

export function redirectToDetail(entityInstance, { state = {} }) {
  const parentRoute = Routes.Private.Detail;

  const finalState = {
    ...state,
    ...(state?.actionOrigin ? {} : { actionOrigin: entityInstance?.actionOrigin || '' }),
  };

  const routeDetails = entityInstance?.schema
    ? getDetailRouteFromSchema(entityInstance, parentRoute)
    : getDetailRouteFromType(entityInstance, parentRoute);

  if (routeDetails) {
    redirectTo(getLink(
      routeDetails?.detailPageUrl,
      routeDetails?.params,
    ), finalState);
  }
}

export const createRedirectFromCollection = ({
  beforeRedirect,
  carousel,
  pageId: page,
  carouselCommonTagEvent,
}) => ({
  item: collection,
  index,
}) => {
  const isLander = isLanderCollection(collection);
  if (beforeRedirect) {
    beforeRedirect(isLander, { index, collection });
  }

  const { text, target } = getWebAction(collection);
  const {
    title,
    sportIds,
    type,
    schema,
  } = collection;

  const state = {
    providerName: title,
    parentLander: getParentLander(page),
    carouselTitle: carousel?.title,
  };

  if (carouselCommonTagEvent) {
    carouselCommonTagEvent({
      eventName: GTM_EVENT_NAMES.CAROUSELS.WATCH_CAROUSELS,
      extraData: {
        transmissionType: transmissionContentType(type || schema),
        sectionName: carousel?.title,
        gtmPlaySource: formatPlaySource({
          parentLander:
            state?.providerName ? state.parentLander : getPlaySourcePage(page),
          section: carousel?.title,
          provider: isLander ? state?.providerName : undefined,
        }),
        providerName: isLander ? state?.providerName : undefined,
        collectionName: isLander ? undefined : state?.providerName,
      },
    });
  }

  if (!target) {
    return;
  }
  if (isLander) {
    redirectTo(
      getLink(
        Routes.Private.Lander,
        {
          pageId: target,
          query: text && {
            source: page,
            landerName: title,
          },
        },
      ),
      state,
    );
  } else {
    redirectTo(
      getLink(Routes.Private.Catalog, {
        parentCarouselType: carousel?.type,
        parentCarouselId: carousel?.sourceCarouselId,
        bannerId: target,
        query: sportIds,
      }),
      state,
    );
  }
};

export function previousUrlAvailable() {
  return history.length > 1;
}

export function goBack() {
  if (history.length > 1) {
    history.goBack();
  } else {
    history.push('/');
  }
}
